export const DATASET_SHAPES = {
  STAGE_FLOW: 'stage_flow',
  TIME_SERIES: 'time_series',
  CATEGORICAL_COMPARISON: 'categorical_comparison',
  MATRIX: 'matrix',
  DISTRIBUTION: 'distribution',
  KPI: 'kpi',
  GOAL_PROGRESS: 'goal_progress',
  GEO: 'geo',
  RADAR: 'radar',
  TIMELINE: 'timeline',
  STATUS_SEGMENTS: 'status_segments',
  ACTION_TABLE: 'action_table',
  SEGMENTED_BARS: 'segmented_bars'
};

const hasKeys = (value, keys = []) => keys.every((key) => value && Object.prototype.hasOwnProperty.call(value, key));

const toDateKey = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const validators = {
  [DATASET_SHAPES.STAGE_FLOW]: (dataset) => {
    const stages = dataset?.stages || [];
    const exits = dataset?.exits || [];
    if (!stages.length) return ['Stage flow datasets require at least one stage.'];
    const errors = [];
    let previousCount = null;

    stages.forEach((stage, index) => {
      if (!hasKeys(stage, ['id', 'label', 'count', 'conversionRate', 'dropoffCount', 'dropoffRate'])) {
        errors.push(`Stage ${index} is missing required fields.`);
      }
      if (typeof stage.count !== 'number' || stage.count < 0) {
        errors.push(`Stage ${stage.id || index} has an invalid count.`);
      }
      if (previousCount !== null && stage.count > previousCount) {
        errors.push(`Stage ${stage.id || index} has a count greater than the previous stage.`);
      }
      previousCount = stage.count;
    });

    const stageIds = new Set(stages.map((stage) => stage.id));
    exits.forEach((exit, index) => {
      if (!hasKeys(exit, ['id', 'sourceStageId', 'label', 'count', 'rate'])) {
        errors.push(`Exit ${index} is missing required fields.`);
        return;
      }
      if (!stageIds.has(exit.sourceStageId)) {
        errors.push(`Exit ${exit.id || index} points to an unknown source stage.`);
      }
      if (typeof exit.count !== 'number' || exit.count < 0) {
        errors.push(`Exit ${exit.id || index} has an invalid count.`);
      }
    });

    return errors;
  },
  [DATASET_SHAPES.TIME_SERIES]: (dataset) => {
    const periods = dataset?.periods || [];
    const series = dataset?.series || [];
    if (!periods.length) return ['Time-series datasets require periods.'];
    if (!series.length) return ['Time-series datasets require at least one series.'];

    const seen = new Set();
    const errors = [];
    let previousDate = null;

    periods.forEach((period) => {
      if (!hasKeys(period, ['key', 'label', 'date'])) {
        errors.push('A time-series period is missing required fields.');
        return;
      }
      const normalizedDate = toDateKey(period.date);
      if (!normalizedDate) {
        errors.push(`Time-series period ${period.key} has an invalid date.`);
        return;
      }
      if (seen.has(period.key)) {
        errors.push(`Time-series period ${period.key} is duplicated.`);
      }
      if (previousDate && normalizedDate <= previousDate) {
        errors.push(`Time-series period ${period.key} is not strictly ordered.`);
      }
      seen.add(period.key);
      previousDate = normalizedDate;
    });

    series.forEach((entry) => {
      if (!hasKeys(entry, ['id', 'label', 'values'])) {
        errors.push('A time-series entry is missing required fields.');
        return;
      }
      if (!Array.isArray(entry.values) || entry.values.length !== periods.length) {
        errors.push(`Time-series entry ${entry.id} does not align with periods.`);
      }
    });

    return errors;
  },
  [DATASET_SHAPES.CATEGORICAL_COMPARISON]: (dataset) => {
    const categories = dataset?.categories || [];
    const metrics = dataset?.metrics || [];
    if (!categories.length) return ['Categorical comparison datasets require categories.'];
    if (!metrics.length) return ['Categorical comparison datasets require metric definitions.'];
    const errors = [];

    categories.forEach((category) => {
      if (!hasKeys(category, ['id', 'label', 'metrics'])) {
        errors.push('A categorical comparison row is missing required fields.');
        return;
      }
      metrics.forEach((metric) => {
        if (!Object.prototype.hasOwnProperty.call(category.metrics, metric.id)) {
          errors.push(`Category ${category.id} is missing metric ${metric.id}.`);
        }
      });
    });

    return errors;
  },
  [DATASET_SHAPES.MATRIX]: (dataset) => {
    const rows = dataset?.rows || [];
    const columns = dataset?.columns || [];
    const cells = dataset?.cells || [];
    if (!rows.length || !columns.length) return ['Matrix datasets require rows and columns.'];
    if (!cells.length) return ['Matrix datasets require cells.'];

    const rowIds = new Set(rows.map((row) => row.id));
    const columnIds = new Set(columns.map((column) => column.id));
    return cells.reduce((errors, cell) => {
      if (!hasKeys(cell, ['rowId', 'columnId', 'value'])) {
        errors.push('A matrix cell is missing required fields.');
      }
      if (!rowIds.has(cell.rowId) || !columnIds.has(cell.columnId)) {
        errors.push(`Matrix cell ${cell.rowId}/${cell.columnId} does not map to a valid row/column.`);
      }
      return errors;
    }, []);
  },
  [DATASET_SHAPES.DISTRIBUTION]: (dataset) => {
    const points = dataset?.points || [];
    if (!points.length) return ['Distribution datasets require points.'];
    return points.reduce((errors, point) => {
      if (!hasKeys(point, ['id', 'label', 'x', 'y'])) {
        errors.push('A distribution point is missing required fields.');
      }
      return errors;
    }, []);
  },
  [DATASET_SHAPES.KPI]: (dataset) => {
    if (!hasKeys(dataset, ['value', 'unit'])) {
      return ['KPI datasets require value and unit.'];
    }
    return [];
  },
  [DATASET_SHAPES.GOAL_PROGRESS]: (dataset) => {
    const periods = dataset?.periods || [];
    const items = dataset?.items || [];
    if (!items.length) return ['Goal progress datasets require items.'];
    if (!periods.length) return ['Goal progress datasets require periods.'];
    return items.reduce((errors, item) => {
      if (!hasKeys(item, ['id', 'label', 'target', 'actual', 'pipeline', 'percentComplete', 'daysRemaining'])) {
        errors.push('A goal progress item is missing required fields.');
        return errors;
      }
      if (!Array.isArray(item.history) || item.history.length !== periods.length) {
        errors.push(`Goal progress item ${item.id || item.label} is missing period history.`);
        return errors;
      }
      item.history.forEach((entry) => {
        if (!hasKeys(entry, ['periodKey', 'target', 'actual'])) {
          errors.push(`Goal progress history for ${item.id || item.label} is missing required fields.`);
        }
      });
      return errors;
    }, []);
  },
  [DATASET_SHAPES.GEO]: (dataset) => {
    const countries = dataset?.countries || [];
    if (!countries.length) return ['Geo datasets require countries.'];
    return countries.reduce((errors, country) => {
      if (!hasKeys(country, ['id', 'label', 'regionId', 'applicationVolume', 'conversionRate', 'cities'])) {
        errors.push('A geo country is missing required fields.');
      }
      return errors;
    }, []);
  },
  [DATASET_SHAPES.RADAR]: (dataset) => {
    const axes = dataset?.axes || [];
    const profiles = dataset?.profiles || [];
    if (!axes.length) return ['Radar datasets require axes.'];
    if (!profiles.length) return ['Radar datasets require at least one profile.'];
    const axisIds = new Set(axes.map((axis) => axis.id));
    const errors = [];

    axes.forEach((axis, index) => {
      if (!hasKeys(axis, ['id', 'label', 'maximum'])) {
        errors.push(`Radar axis ${index} is missing required fields.`);
      }
    });

    profiles.forEach((profile, index) => {
      if (!hasKeys(profile, ['id', 'label', 'values'])) {
        errors.push(`Radar profile ${index} is missing required fields.`);
        return;
      }
      axes.forEach((axis) => {
        if (!Object.prototype.hasOwnProperty.call(profile.values || {}, axis.id)) {
          errors.push(`Radar profile ${profile.id} is missing axis ${axis.id}.`);
        }
      });
      Object.keys(profile.values || {}).forEach((axisId) => {
        if (!axisIds.has(axisId)) {
          errors.push(`Radar profile ${profile.id} includes unknown axis ${axisId}.`);
        }
      });
    });

    return errors;
  },
  [DATASET_SHAPES.TIMELINE]: (dataset) => {
    const rows = dataset?.rows || [];
    if (!rows.length) return ['Timeline datasets require rows.'];
    return rows.reduce((errors, row, index) => {
      if (!hasKeys(row, ['id', 'label', 'targetDate', 'predictedDate', 'confidenceStartDate', 'confidenceEndDate', 'status'])) {
        errors.push(`Timeline row ${index} is missing required fields.`);
      }
      return errors;
    }, []);
  },
  [DATASET_SHAPES.STATUS_SEGMENTS]: (dataset) => {
    const lanes = dataset?.lanes || [];
    if (!lanes.length) return ['Status segment datasets require lanes.'];
    return lanes.reduce((errors, lane, laneIndex) => {
      if (!hasKeys(lane, ['id', 'label', 'segments'])) {
        errors.push(`Status lane ${laneIndex} is missing required fields.`);
        return errors;
      }
      (lane.segments || []).forEach((segment, segmentIndex) => {
        if (!hasKeys(segment, ['id', 'label', 'daysOpen', 'roleLabel'])) {
          errors.push(`Status segment ${lane.id || laneIndex}/${segmentIndex} is missing required fields.`);
        }
      });
      return errors;
    }, []);
  },
  [DATASET_SHAPES.ACTION_TABLE]: (dataset) => {
    const columns = dataset?.columns || [];
    const rows = dataset?.rows || [];
    if (!columns.length) return ['Action table datasets require columns.'];
    if (!rows.length) return ['Action table datasets require rows.'];
    return rows.reduce((errors, row, rowIndex) => {
      if (!hasKeys(row, ['id', 'label', 'values'])) {
        errors.push(`Action table row ${rowIndex} is missing required fields.`);
        return errors;
      }
      columns.forEach((column) => {
        if (!Object.prototype.hasOwnProperty.call(row.values || {}, column.id)) {
          errors.push(`Action table row ${row.id || rowIndex} is missing column ${column.id}.`);
        }
      });
      return errors;
    }, []);
  },
  [DATASET_SHAPES.SEGMENTED_BARS]: (dataset) => {
    const rows = dataset?.rows || [];
    const segments = dataset?.segments || [];
    if (!rows.length) return ['Segmented bar datasets require rows.'];
    if (!segments.length) return ['Segmented bar datasets require segment definitions.'];
    return rows.reduce((errors, row, rowIndex) => {
      if (!hasKeys(row, ['id', 'label', 'segments', 'lineValue'])) {
        errors.push(`Segmented bar row ${rowIndex} is missing required fields.`);
        return errors;
      }
      segments.forEach((segment) => {
        if (!Object.prototype.hasOwnProperty.call(row.segments || {}, segment.id)) {
          errors.push(`Segmented bar row ${row.id || rowIndex} is missing segment ${segment.id}.`);
        }
      });
      return errors;
    }, []);
  }
};

export const validateDataset = (dataset) => {
  const shape = dataset?.shape;
  if (!shape || !validators[shape]) {
    return { valid: false, errors: ['Unsupported dataset shape.'] };
  }
  const errors = validators[shape](dataset);
  return { valid: errors.length === 0, errors };
};

export const createStageFlowDataset = ({ title = '', summary = {}, stages = [], exits = [], tone = 'accent' }) => ({
  shape: DATASET_SHAPES.STAGE_FLOW,
  title,
  summary,
  tone,
  stages,
  exits
});

export const createTimeSeriesDataset = ({ title = '', unit = '', periods = [], series = [], summary = {} }) => ({
  shape: DATASET_SHAPES.TIME_SERIES,
  title,
  unit,
  periods,
  series,
  summary
});

export const createCategoricalComparisonDataset = ({
  title = '',
  metrics = [],
  categories = [],
  summary = {},
  tableColumns = [],
  selectors = [],
  selectedViewId = '',
  views = {}
}) => ({
  shape: DATASET_SHAPES.CATEGORICAL_COMPARISON,
  title,
  metrics,
  categories,
  summary,
  tableColumns,
  selectors,
  selectedViewId,
  views
});

export const createMatrixDataset = ({ title = '', rows = [], columns = [], cells = [], summary = {} }) => ({
  shape: DATASET_SHAPES.MATRIX,
  title,
  rows,
  columns,
  cells,
  summary
});

export const createDistributionDataset = ({ title = '', points = [], summary = {} }) => ({
  shape: DATASET_SHAPES.DISTRIBUTION,
  title,
  points,
  summary
});

export const createKpiDataset = ({
  title = '',
  value = 0,
  unit = '',
  trendValue = 0,
  trendLabel = '',
  benchmark = null,
  trendSeries = null,
  minimum = 0,
  maximum = 100
}) => ({
  shape: DATASET_SHAPES.KPI,
  title,
  value,
  unit,
  trendValue,
  trendLabel,
  benchmark,
  trendSeries,
  minimum,
  maximum
});

export const createGoalProgressDataset = ({ title = '', periods = [], items = [], summary = {} }) => ({
  shape: DATASET_SHAPES.GOAL_PROGRESS,
  title,
  periods,
  items,
  summary
});

export const createGeoDataset = ({
  title = '',
  regions = [],
  countries = [],
  summary = {},
  defaultRegionId = '',
  defaultCountryId = ''
}) => ({
  shape: DATASET_SHAPES.GEO,
  title,
  regions,
  countries,
  summary,
  defaultRegionId,
  defaultCountryId
});

export const createRadarDataset = ({
  title = '',
  axes = [],
  profiles = [],
  summary = {},
  selectors = [],
  selectedViewId = '',
  views = {}
}) => ({
  shape: DATASET_SHAPES.RADAR,
  title,
  axes,
  profiles,
  summary,
  selectors,
  selectedViewId,
  views
});

export const createTimelineDataset = ({ title = '', rows = [], summary = {} }) => ({
  shape: DATASET_SHAPES.TIMELINE,
  title,
  rows,
  summary
});

export const createStatusSegmentsDataset = ({ title = '', lanes = [], summary = {} }) => ({
  shape: DATASET_SHAPES.STATUS_SEGMENTS,
  title,
  lanes,
  summary
});

export const createActionTableDataset = ({ title = '', columns = [], rows = [], summary = {}, selectors = [], selectedViewId = '', views = {} }) => ({
  shape: DATASET_SHAPES.ACTION_TABLE,
  title,
  columns,
  rows,
  summary,
  selectors,
  selectedViewId,
  views
});

export const createSegmentedBarsDataset = ({
  title = '',
  rows = [],
  segments = [],
  line = {},
  summary = {},
  selectors = [],
  selectedViewId = '',
  views = {}
}) => ({
  shape: DATASET_SHAPES.SEGMENTED_BARS,
  title,
  rows,
  segments,
  line,
  summary,
  selectors,
  selectedViewId,
  views
});
